
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, Wand2, Crown } from 'lucide-react';
import { Bateau } from '@/hooks/useBateaux';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useProAccess } from '@/hooks/useProAccess';
import { useListesAvitaillement, ArticleAvitaillement } from '@/hooks/useListesAvitaillement';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface GenerateurAvitaillementProps {
  bateaux: Bateau[];
  selectedBateauId?: string;
  onRefresh?: () => void;
}

const GenerateurAvitaillement = ({ bateaux, selectedBateauId, onRefresh }: GenerateurAvitaillementProps) => {
  const [navire, setNavire] = useState(selectedBateauId || '');
  const [nombrePersonnes, setNombrePersonnes] = useState('2');
  const [nombreJours, setNombreJours] = useState('3');
  const [saison, setSaison] = useState('inter-saison');
  const [filters, setFilters] = useState({
    alimentaire: true,
    boissons: true,
    hygiene: false
  });
  const [regimes, setRegimes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { isProAccount } = useProAccess();
  const { creerListe } = useListesAvitaillement();

  const handleFilterChange = (filterName: keyof typeof filters, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: checked
    }));
  };

  const handleRegimeChange = (regime: string, checked: boolean) => {
    setRegimes(prev => 
      checked 
        ? [...prev, regime]
        : prev.filter(r => r !== regime)
    );
  };

  const genererListe = async () => {
    if (!isProAccount) {
      toast({
        title: "Fonctionnalité Premium",
        description: "Le générateur d'avitaillement est disponible avec l'abonnement Premium.",
        variant: "destructive",
      });
      return;
    }

    if (!navire) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un navire.",
        variant: "destructive",
      });
      return;
    }

    // Trouver le nom du navire à partir de son ID
    const bateauSelectionne = bateaux.find(b => b.id === navire);
    const nomNavire = bateauSelectionne ? bateauSelectionne.nom : 'Navire inconnu';

    setLoading(true);
    try {
      console.log('Génération de la liste d\'avitaillement...');
      console.log('Paramètres envoyés:', {
        navire: nomNavire,
        nombrePersonnes: parseInt(nombrePersonnes),
        nombreJours: parseInt(nombreJours),
        saison,
        filtres: {
          alimentaire: filters.alimentaire,
          boissons: filters.boissons,
          hygiene: filters.hygiene,
          regimes: regimes
        }
      });
      
      const { data, error } = await supabase.functions.invoke('generer-avitaillement', {
        body: {
          navire: nomNavire, // Envoyer le nom au lieu de l'ID
          nombrePersonnes: parseInt(nombrePersonnes),
          nombreJours: parseInt(nombreJours),
          saison,
          filtres: {
            alimentaire: filters.alimentaire,
            boissons: filters.boissons,
            hygiene: filters.hygiene,
            regimes: regimes
          }
        }
      });

      if (error) {
        console.error('Erreur lors de la génération:', error);
        toast({
          title: "Erreur",
          description: "Impossible de générer la liste d'avitaillement.",
          variant: "destructive",
        });
        return;
      }

      console.log('Données reçues de l\'API:', data);
      
      if (!data || !data.articles || !Array.isArray(data.articles)) {
        console.error('Pas d\'articles dans la réponse:', data);
        toast({
          title: "Erreur",
          description: "Aucun article généré dans la liste.",
          variant: "destructive",
        });
        return;
      }

      console.log('Articles générés par l\'IA:', data.articles.length);
      
      // Convertir au format ArticleAvitaillement pour la liste
      const articlesAvitaillement: ArticleAvitaillement[] = data.articles.map((article: any) => ({
        nom: article.nom || 'Article',
        categorie: article.categorie || 'Alimentation',
        quantite: Math.ceil(article.quantite_max || 1),
        unite: article.unite || 'unité',
        priorite: 'normale' as const
      }));

      console.log('Articles formatés pour liste d\'avitaillement:', articlesAvitaillement.length);

      // Créer le nom de la liste
      const bateauSelectionne = bateaux.find(b => b.id === navire);
      const nomBateau = bateauSelectionne ? bateauSelectionne.nom : 'Navire';
      const nomListe = `${nomBateau} - ${nombrePersonnes}p ${nombreJours}j - ${format(new Date(), 'dd/MM/yy', { locale: fr })}`;

      // Sauvegarder la liste d'avitaillement
      const resultatListe = await creerListe(
        nomListe,
        navire,
        articlesAvitaillement,
        parseInt(nombrePersonnes),
        parseInt(nombreJours)
      );

      if (resultatListe.error) {
        console.error('Erreur lors de la création de la liste:', resultatListe.error);
        toast({
          title: "Erreur",
          description: "Impossible de sauvegarder la liste d'avitaillement.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Succès",
        description: `Liste "${nomListe}" créée avec ${articlesAvitaillement.length} articles !`,
      });

      console.log('Liste d\'avitaillement créée avec succès');

      // Déclencher le refresh du parent si fourni
      if (onRefresh) {
        onRefresh();
      }

    } catch (err) {
      console.error('Erreur:', err);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={!isProAccount ? "opacity-60" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wand2 className="h-5 w-5" />
          Générateur de liste d'avitaillement
          {!isProAccount && <Crown className="h-4 w-4 text-nautic-gold" />}
        </CardTitle>
        {!isProAccount && (
          <p className="text-sm text-nautic-gold font-medium flex items-center gap-1">
            <Crown className="h-3 w-3" />
            Fonctionnalité disponible avec l'abonnement Premium
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="navire">Navire</Label>
            <Select value={navire} onValueChange={setNavire} disabled={!isProAccount}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un bateau" />
              </SelectTrigger>
              <SelectContent>
                {bateaux.map((bateau) => (
                  <SelectItem key={bateau.id} value={bateau.id}>
                    {bateau.nom} - {bateau.nom_constructeur} {bateau.nom_modele}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="personnes">Nombre de personnes</Label>
            <Select value={nombrePersonnes} onValueChange={setNombrePersonnes} disabled={!isProAccount}>
              <SelectTrigger>
                <SelectValue placeholder="Personnes" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((nb) => (
                  <SelectItem key={nb} value={nb.toString()}>
                    {nb} personne{nb > 1 ? 's' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="jours">Nombre de jours</Label>
            <Select value={nombreJours} onValueChange={setNombreJours} disabled={!isProAccount}>
              <SelectTrigger>
                <SelectValue placeholder="Jours" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 14, 21].map((nb) => (
                  <SelectItem key={nb} value={nb.toString()}>
                    {nb} jour{nb > 1 ? 's' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="saison">Période</Label>
            <Select value={saison} onValueChange={setSaison} disabled={!isProAccount}>
              <SelectTrigger>
                <SelectValue placeholder="Saison" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="été">Été</SelectItem>
                <SelectItem value="hiver">Hiver</SelectItem>
                <SelectItem value="inter-saison">Inter-saison</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Filtres catégories */}
        <div className="space-y-2">
          <Label>Catégories</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="alimentaire"
                checked={filters.alimentaire}
                onCheckedChange={(checked) => handleFilterChange('alimentaire', checked as boolean)}
                disabled={!isProAccount}
              />
              <Label htmlFor="alimentaire" className="text-sm">Alimentaire</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="boissons"
                checked={filters.boissons}
                onCheckedChange={(checked) => handleFilterChange('boissons', checked as boolean)}
                disabled={!isProAccount}
              />
              <Label htmlFor="boissons" className="text-sm">Boissons</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hygiene"
                checked={filters.hygiene}
                onCheckedChange={(checked) => handleFilterChange('hygiene', checked as boolean)}
                disabled={!isProAccount}
              />
              <Label htmlFor="hygiene" className="text-sm">Hygiène</Label>
            </div>
          </div>
        </div>

        {/* Filtres régimes */}
        <div className="space-y-2">
          <Label>Régimes alimentaires</Label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { id: 'vegetarien', label: 'Végétarien' },
              { id: 'vegan', label: 'Vegan' },
              { id: 'keto', label: 'Kéto' },
              { id: 'sans-gluten', label: 'Sans gluten' },
              { id: 'paleo', label: 'Paléo' }
            ].map((regime) => (
              <div key={regime.id} className="flex items-center space-x-2">
                <Checkbox
                  id={regime.id}
                  checked={regimes.includes(regime.id)}
                  onCheckedChange={(checked) => handleRegimeChange(regime.id, checked as boolean)}
                  disabled={!isProAccount}
                />
                <Label htmlFor={regime.id} className="text-sm">{regime.label}</Label>
              </div>
            ))}
          </div>
        </div>

        <Button 
          onClick={genererListe} 
          disabled={loading || !navire || !isProAccount}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <Wand2 className="h-4 w-4 mr-2" />
              {isProAccount ? "Créer la liste d'avitaillement" : (
                <>
                  <Crown className="h-4 w-4 mr-2 text-nautic-gold" />
                  Abonnement Premium requis
                </>
              )}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GenerateurAvitaillement;
